import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { LEGACY_DATASETS_TOOLTIP } from 'webapp/enums/metric-mover';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, isAlive } from 'webapp/utils/common';

import type { ActionOption } from 'webapp/components/form/select-with-action';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';

import type Source from 'webapp/models-custom/source';
import type SourceType from 'webapp/models-custom/source-type';

import type MetricMoverService from 'webapp/services/metric-mover';
import type { SourceListEntry } from 'webapp/services/model-manager/sources';

// a source list entry as the select shows it: a numeric value, and legacy sources disabled with a tooltip
type SourceSelectOption = Omit<SourceListEntry, 'value'> & {
	value: string | number;
	description?: string;
	disabled?: string;
};

interface Args {
	sourceId?: number;
	isDisabled?: boolean;
	isCloning?: boolean;
	onChange: (sourceId: number) => void;
	fromWizard?: boolean;
}

export default class CustomQuerySetupCommonSourceSelectComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('billing') declare billingService: Service.Billing;
	@service('metric-mover') declare metricMoverService: MetricMoverService;

	get source(): Source | null {
		if (!this.args.sourceId) {
			return null;
		}
		return this.mmSourcesService.findModel(String(this.args.sourceId)) ?? null;
	}

	get sourceType(): SourceType | null {
		const sourceTypeId = this.source?.type;
		if (!sourceTypeId) {
			return null;
		}
		return this.mmSourceTypesService.findModelById(sourceTypeId) ?? null;
	}

	get showSourceGroupConnect(): boolean {
		return !this.args.fromWizard;
	}

	@cached
	get datasourceList() {
		const enabledTypes = this.mmSourceTypesService.connectableSourceTypeModels
			.filter((sourceType: SourceType) => sourceType.isQBSupported)
			.map((item) => item.id);

		const horizonEnabledTypes = this.mmSourceTypesService.connectableSourceTypeModels
			.filter((sourceType: SourceType) => sourceType.isHorizonSupported)
			.map((item) => item.id);

		const canCreateSQLLegacyMetrics = this.billingService.checkIfCan(
			BillingFeature.SQL_LEGACY_METRICS,
		);

		const canCreateGSheetsLegacyMetrics = this.billingService.checkIfCan(
			BillingFeature.GSHEETS_LEGACY_METRICS,
		);

		const selectedSourceIdString = this.args.sourceId ? String(this.args.sourceId) : null;

		let datasources = this.mmSourcesService.sourceListSpecific.filter((source) => {
			// Always keep the currently-selected source visible so edit mode shows it,
			// even when it would otherwise be filtered out (e.g. a legacy source).
			if (selectedSourceIdString && source.value === selectedSourceIdString) {
				return true;
			}

			if (source.isSQLType && !source.isDataset) {
				return canCreateSQLLegacyMetrics;
			} else if (source.isSheets && !source.isDataset) {
				return canCreateGSheetsLegacyMetrics;
			}

			return (
				enabledTypes.includes(source.type) ||
				horizonEnabledTypes.includes(source.type) ||
				source.isDataset ||
				source.isMergedDataset
			);
		});

		// when cloning, only show sources of the same type
		const sourceTypeId = this.source?.sourceType?.id;
		if (sourceTypeId && this.args.isCloning) {
			datasources = datasources.filter((source: SomeDataObject) => {
				return source.type === sourceTypeId;
			});
		}

		const parsedDatasources = datasources.map((source) => {
			const sourceCopy: SourceSelectOption = copyObject(source);

			// remove ↳ icon if the dataset parent itself is not on the list
			if (sourceCopy.parentSpaceAccessId && sourceCopy.prefixIcon) {
				const saidString = String(sourceCopy.parentSpaceAccessId);
				const parentSource = datasources.find((s) => s.value === saidString);
				if (!parentSource) {
					sourceCopy.prefixIcon = undefined;
				}
			}

			// In deprecation mode only legacy custom sources (SQL, spreadsheets and
			// custom push / API-token metrics) are disabled with a tooltip pointing users
			// to datasets — the same set that shows the "legacy integration" warning
			// (Source.isLegacyCustomSource). Datasets, other integrations and the
			// currently-selected source stay usable.
			if (this.metricMoverService.isLegacyDeprecationActive) {
				const sourceModel = this.mmSourcesService.findModel(String(sourceCopy.value));
				const isSelected =
					!!selectedSourceIdString && sourceCopy.value === selectedSourceIdString;
				if (sourceModel?.isLegacyCustomSource && !isSelected) {
					sourceCopy.isDisabled = true;
					sourceCopy.description = LEGACY_DATASETS_TOOLTIP;
				}
			}

			sourceCopy.value = +sourceCopy.value;
			sourceCopy.disabled = sourceCopy.isDisabled ? 'disabled' : '';

			return sourceCopy;
		});

		return parsedDatasources as LabelValuePairNumeric[];
	}

	get dropdownActions(): ActionOption[] {
		const actions: ActionOption[] = [];
		if (!this.args.fromWizard && !this.args.isCloning) {
			actions.push({
				id: null,
				text: 'Add data source',
				isSpecial: true, // don't count in tabbed dropdowns
				onClick: () => {
					this.navigationService.transitionToRoute('datamanager.available');
				},
				html: '<a class="source-add">Add data source</a>',
				position: 'bottom',
			});
		}
		return actions;
	}

	@action
	connectNewSourceFromDropdown(datasourceName: string): void {
		const datasourcetype = this.mmSourceTypesService.sourceTypeModels.find(
			(item) => item.name === datasourceName,
		);
		if (datasourcetype) {
			void datasourcetype.connectNew({ origin: 'querybuilderForm' }).then((result) => {
				if (result.isSuccess && isAlive(this)) {
					const sourceInfo = (result as SomeDataObject).source;
					this.args.onChange(sourceInfo.id);
				}
			});
		}
	}

	@action
	changeSource(value: number | null): void {
		const source = this.mmSourcesService.findModel(String(value));
		if (source) {
			this.analyticsService.event(TrackingEvent.QUERY_SOURCE_SELECTED, {
				datasource: source.sourceType.key,
			});
		}

		this.args.onChange(value!);
	}
	<template>
		<div class="row items datasource">
			<div class="item size-2-x2">

				<FormSelectWithActionComponent
					name="datasource"
					@selected={{@sourceId}}
					@options={{this.datasourceList}}
					@actionOptions={{this.dropdownActions}}
					@onChange={{this.changeSource}}
					@placeholder="Data source"
					@isRequired={{true}}
					@isDisabled={{@isDisabled}}
					@searchInLabel2={{true}}
					@onConnectNewSource={{this.connectNewSourceFromDropdown}}
					@theme="dark"
					@showLabel3={{true}}
					@tooltipOnListFromProperty="description"
				/>

			</div>

			{{#unless @sourceId}}

				<div class="empty-placeholder clear">
					Start by selecting a data source or dataset
				</div>

			{{/unless}}

		</div>
	</template>
}
